import { Box, IconButton } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useBaseGrid } from "../../BaseGrid/Hooks/useBaseGrid";
import BaseGridDropDownFilter from "../../BaseGrid/BaseGridCommonFilters/BaseGridDropDownFilter";
import BaseGrid from "../../BaseGrid/BaseGrid";
import { PoliciesFilterProps } from "./TypesAgencyPolicies";
import {
  getDetaultTableFilterValues,
  getUpdatedDateFilter,
} from "./FunctionsAgencyPolicies";
import { OptionsContextActionParameterProperties } from "../../TrueUI/Tables/BaseTable2/TableProperties";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  agencyPolicyFilterOptions,
  tableColumnOptions,
  toolbarOptions,
} from "./ConstantsAgencyPolicies";
import { BaseGridProperties } from "../../BaseGrid/BaseGridProperties";
import { GettingDateWithoutTime } from "../../../utilities/dateFunctions";
import AgencyPoliciesTotalPremium from "./AgencyPoliciesTotalPremium";
import BaseGridDateDisabledValidationFilter from "./BaseGridDateDisabledValidationFilter";
import { useNavigate } from "react-router";
import { getQueryStringsURL } from "../../../utilities/URLUtilities_OBSOLETE";
import { INSURED_BODY_SECTION } from "../../../utilities/queryStringsHash";
import { SubSideNavItemDestinationsEnum } from "../../../dtos/sub-side-nav-item-destinations-enum";

const tableName = "agency_policy_filter_options_table";

const AgencyPolicy: FC = () => {
  const navigate = useNavigate();
  const { agencyId } = useParams();

  const [policiesFilter, setPoliciesFilter] = useState<PoliciesFilterProps>({
    ...getDetaultTableFilterValues(parseInt(agencyId ?? "0")),
  });

  const setFilterStringForUrl = (policiesFilter: PoliciesFilterProps) => {
    setPoliciesFilter({
      ...policiesFilter,
      filterString: `filterId=${policiesFilter.filterId}&agencyID=${agencyId}&fromDate=${policiesFilter.fromDate}&toDate=${policiesFilter.toDate}`,
    });
  };

  useEffect(() => {
    setFilterStringForUrl(policiesFilter);
  }, [
    policiesFilter?.filterId,
    policiesFilter?.fromDate,
    policiesFilter?.toDate,
  ]);

  const actionsForRows = (
    actionOptions: OptionsContextActionParameterProperties<any>
  ) => {
    const historyHashes = getQueryStringsURL([
      {
        nameOfHash: INSURED_BODY_SECTION,
        valueOfHash: SubSideNavItemDestinationsEnum.HISTORY,
      },
    ]);
    const insuredId = actionOptions?.row?.InsuredId ?? null;
    return (
      <IconButton
        aria-label="expand row"
        size="small"
        onClick={() => navigate(`/insured/${insuredId}${historyHashes}`)}
        disabled={insuredId === null}
      >
        <LaunchIcon fontSize="small" />
      </IconButton>
    );
  };

  const tableProperties: BaseGridProperties = {
    name: tableName,
    getURL: `api/Agency/GetAgencyPolicies?${policiesFilter.filterString}`,
    useManuallyReloadParameters: true,
    filterOptions: [
      (options) =>
        BaseGridDropDownFilter({
          options,
          id: "agency-policy-filter-type-id",
          optionsOverride: agencyPolicyFilterOptions,
          defaultValue: policiesFilter?.filterId,
          usingExternalFiltering: true,
          onChange: (value) =>
            setPoliciesFilter({ ...policiesFilter, filterId: value }),
          updateTableFilter: true,
        }),
      (options) =>
        BaseGridDateDisabledValidationFilter({
          options,
          label: "From:",
          inputWidth: "210px",
          defaultValue: GettingDateWithoutTime(
            new Date(policiesFilter?.fromDate ?? "")
          ),
          isStaticFilter: true,
          id: "agency-policy-from-date-filter",
          onChangeRawValue: (value) =>
            setPoliciesFilter({
              ...policiesFilter,
              ...getUpdatedDateFilter(
                "fromDate",
                policiesFilter?.fromDate ?? "",
                value
              ),
            }),
          triggerSearchOnBlur: true,
          tableName: tableName,
          disableAllDates: true,
        }),
      (options) =>
        BaseGridDateDisabledValidationFilter({
          options,
          label: "To:",
          inputWidth: "210px",
          defaultValue: GettingDateWithoutTime(
            new Date(policiesFilter?.toDate ?? "")
          ),
          isStaticFilter: true,
          id: "agency-policy-to-date-filter",
          onChangeRawValue: (value) =>
            setPoliciesFilter({
              ...policiesFilter,
              ...getUpdatedDateFilter(
                "toDate",
                policiesFilter?.toDate ?? "",
                value
              ),
            }),
          triggerSearchOnBlur: true,
          tableName: tableName,
          disableAllDates: true,
        }),
      () => <AgencyPoliciesTotalPremium tableName={tableName} />,
    ],
    toolbarOptions: toolbarOptions,
    columnOptions: tableColumnOptions,
    advancedOptions: {
      optionsColumnConfiguration: {
        isHidden: false,
        optionType: "component",
      },
      optionsContextActions: {
        contextComponent: (actionOptions) => actionsForRows(actionOptions),
      },
    },
  };

  const { manuallyReloadParameters } = useBaseGrid(tableProperties);

  useEffect(() => {
    manuallyReloadParameters();
  }, [policiesFilter?.filterString]);

  return (
    <Box sx={{ maxHeight: "100%", m: 2 }}>
      <BaseGrid name={tableName} />
    </Box>
  );
};

export default AgencyPolicy;
